import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { getDashboardDetail } from "../../api/staticPageApi";
import CommonCard from "../../common/components/CommonCard";
import PendingMessages from "./PendingMessages";
import { PeopleOutline } from "@mui/icons-material";
import theme from "../../theme";
import Calendar from "./Calendar";

const MentorDashboard = () => {
    const dispatch = useDispatch();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
    
    const { loading } = useSelector((state) => state.auth);
    const [dashBoardData, setDashBoardData] = useState(null);

    const getDashboardData = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getDashboardDetail();
            setDashBoardData(response.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || 
                               err.response?.data?.message || 
                               err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    }

    useEffect(() => {
        getDashboardData();
    }, []);

    // Responsive spacing and sizing
    const containerSpacing = isMobile ? 1 : isTablet ? 1.5 : 2;
    const statsCardHeight = isMobile ? 'auto' : '100%';
    
    // Stats card configuration
    const statsCards = [
        {
            color: "#10AE2D",
            value: dashBoardData?.total_students || 0,
            label: "Total Students"
        },
        {
            color: "#FF8D28",
            value: dashBoardData?.total_completed_sessions || 0,
            label: "Total Session Completed"
        },
        {
            color: "#0088FF",
            value: dashBoardData?.total_upcoming_sessions || 0,
            label: "Total Upcoming Sessions"
        }
    ];

    return (
        <Fragment>
            {loading && <LoadingSpinner />}
            
            {dashBoardData?.total_students > 0 ? (
                <Box 
                    sx={{ 
                        px: { xs: 1, sm: 2, md: 0 },
                        py: { xs: 1, sm: 1.5, md: 2 }
                    }}
                >
                    <Grid 
                        container 
                        spacing={containerSpacing}
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' }
                        }}
                    >
                        {/* Left Column - Stats and Messages */}
                        <Grid 
                            item 
                            xs={12} 
                            md={7} 
                            lg={8}
                            xl={8}
                            sx={{
                                mb: { xs: containerSpacing, md: 0 }
                            }}
                        >
                            {/* Stats Cards Section */}
                            <CommonCard
                                paddingBottom="0px"
                                borderRadius={isMobile ? 1 : 2}
                                marginTop="0px"
                                content={
                                    <Grid container spacing={containerSpacing}>
                                        {statsCards.map((card, index) => (
                                            <Grid 
                                                item 
                                                xs={12} 
                                                sm={4} 
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                            >
                                                <CommonCard
                                                    paddingBottom="0px"
                                                    borderRadius={isMobile ? 1 : 2}
                                                    marginTop="0px"
                                                    background="#FAFAFA"
                                                    sx={{
                                                        height: statsCardHeight,
                                                        minHeight: { xs: '100px', sm: '120px' },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    content={
                                                        <Box 
                                                            display="flex" 
                                                            alignItems="center" 
                                                            flexDirection="column"
                                                            sx={{ 
                                                                p: { xs: 1.5, sm: 2 },
                                                                width: '100%'
                                                            }}
                                                        >
                                                            <Typography 
                                                                sx={{ 
                                                                    color: card.color,
                                                                    fontSize: { 
                                                                        xs: 20, 
                                                                        sm: 22, 
                                                                        md: 24,
                                                                        lg: 22
                                                                    },
                                                                    fontWeight: 600,
                                                                    mb: { xs: 0.5, sm: 1 }
                                                                }}
                                                            >
                                                                {card.value}
                                                            </Typography>
                                                            <Typography 
                                                                sx={{ 
                                                                    fontWeight: 600,
                                                                    fontSize: { 
                                                                        xs: 13, 
                                                                        sm: 14, 
                                                                        md: 16,
                                                                        lg: 18
                                                                    },
                                                                    textAlign: "center",
                                                                    lineHeight: 1.3
                                                                }}
                                                            >
                                                                {card.label}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                            
                            {/* Pending Messages Section */}
                            <Box sx={{ mt: containerSpacing }}>
                                <PendingMessages chatList={dashBoardData?.pending_messages} />
                            </Box>
                        </Grid>
                        
                        {/* Right Column - Calendar */}
                        <Grid 
                            item 
                            xs={12} 
                            md={5} 
                            lg={4}
                            xl={4}
                            sx={{
                                mt: { xs: 0, md: 0 }
                            }}
                        >
                            <Box sx={{ height: '100%' }}>
                                <Calendar 
                                    allEvents={dashBoardData?.upcoming_sessions} 
                                    getDashboardData={getDashboardData}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                // Empty State
                <Box
                    sx={{
                        maxWidth: {
                            xs: '95%',
                            sm: '500px',
                            md: '550px'
                        },
                        margin: '0 auto',
                        mt: {
                            xs: 2,
                            sm: 3,
                            md: 4
                        },
                        p: {
                            xs: 2,
                            sm: 3,
                            md: 4
                        },
                        background: 'linear-gradient(145deg, #ffffff 0%, #f0f7ff 100%)',
                        borderRadius: {
                            xs: 2,
                            sm: 3,
                            md: 4
                        },
                        boxShadow: '0 10px 30px rgba(0, 100, 200, 0.15)',
                        textAlign: 'center',
                        border: '1px solid rgba(33, 150, 243, 0.1)',
                    }}
                >
                    <PeopleOutline
                        sx={{
                            fontSize: {
                                xs: 48,
                                sm: 56,
                                md: 64
                            },
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            color: 'white',
                            padding: {
                                xs: 1,
                                sm: 1.2,
                                md: 1.5
                            },
                            borderRadius: '50%',
                            mb: {
                                xs: 1.5,
                                sm: 2,
                                md: 2.5
                            },
                            boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)',
                        }}
                    />
                    <Typography
                        variant="h5"
                        component="h2"
                        gutterBottom
                        sx={{
                            fontWeight: 600,
                            background: theme.palette.secondary.main,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: {
                                xs: '1.25rem',
                                sm: '1.5rem',
                                md: '1.75rem'
                            },
                            mb: {
                                xs: 1,
                                sm: 1.5
                            }
                        }}
                    >
                        Welcome to DCP!
                    </Typography>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: theme.palette.primary.main, 
                            lineHeight: 1.6,
                            fontSize: {
                                xs: '0.875rem',
                                sm: '1rem'
                            },
                            px: {
                                xs: 1,
                                sm: 2
                            }
                        }}
                    >
                        Your dashboard is a blank slate for now. It will automatically populate with insights and data once students are assigned to your program.
                    </Typography>
                </Box>
            )}
        </Fragment>
    )
};

export default MentorDashboard;